import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { MaskLines } from "../Reveal";
import { catLabel, catLines, fmtDate } from "../../lib/api";

const TONES = {
  cream: { bg: "bg-cream text-ink", rule: "border-ink", meta: "text-ink/70", hover: "group-hover:text-rose" },
  blush: { bg: "bg-blush text-ink", rule: "border-ink", meta: "text-ink/75", hover: "group-hover:text-rose" },
  ink: { bg: "flip bg-ink text-cream", rule: "border-cream", meta: "text-cream/75", hover: "group-hover:text-hot" },
};
const PAD = "max(1.25rem, calc((100vw - 1440px) / 2 + 2.5rem))";
const RAIL_PAD = { paddingLeft: PAD, scrollPaddingLeft: PAD, paddingRight: "1.25rem" };

const RailCard = ({ a, i, big, t, cat }) => (
  <Link
    to={`/article/${a.slug}`}
    className={`group snap-start shrink-0 ${big ? "w-[84vw] sm:w-[500px]" : "w-[70vw] sm:w-[330px]"}`}
    data-testid={`rail-${cat}-card-${i}`}
  >
    <div className={`img-zoom relative overflow-hidden bg-blush ${big ? "aspect-[4/5]" : "aspect-[3/4]"}`}>
      <img src={a.cover} alt={a.title} loading="lazy" className="h-full w-full object-cover" />
      <span className="absolute top-3 left-3 wordmark text-pink text-5xl leading-[0.75] pb-[0.15em] drop-shadow-[0_2px_0_#1B1215]">
        {String(i + 1).padStart(2, "0")}
      </span>
    </div>
    <p className={`kicker mt-4 ${t.meta}`}>{fmtDate(a.published)} · {a.read_time} min</p>
    <h3 className={`display mt-2 transition-colors ${t.hover} ${big ? "text-4xl sm:text-5xl" : "text-2xl sm:text-3xl"}`}>{a.title}</h3>
  </Link>
);

export const CategoryRail = ({ cat, items = [], index = 1, tone = "cream" }) => {
  if (!items.length) return null;
  const t = TONES[tone];
  return (
    <section className={`${t.bg} py-16 sm:py-24 overflow-hidden`} data-testid={`rail-${cat}`}>
      <div className="max-w-[1440px] mx-auto px-5 sm:px-10">
        <div className={`flex items-end justify-between gap-6 border-b-[3px] ${t.rule} pb-3 mb-8`}>
          <h2 className="display text-[clamp(3.5rem,10vw,9rem)] leading-[0.8]">
            <MaskLines lines={catLines(cat)} inView testId={`rail-${cat}-title`} />
          </h2>
          <div className="text-right shrink-0">
            <span className="wordmark text-pink text-5xl sm:text-6xl leading-[0.75] inline-block pb-[0.15em]">{String(index).padStart(2, "0")}</span>
            <Link to={`/category/${cat}`} className="group kicker mt-2 flex items-center gap-1.5 justify-end hover:opacity-60 transition-opacity" data-testid={`rail-${cat}-all`}>
              {cat === "art" ? "All" : `All ${catLabel(cat)}`} <ArrowUpRight className="h-3.5 w-3.5 group-hover:rotate-45 transition-transform" />
            </Link>
          </div>
        </div>
      </div>
      <div className="flex items-start gap-5 sm:gap-8 overflow-x-auto snap-x snap-mandatory no-scrollbar pb-2" style={RAIL_PAD} data-testid={`rail-${cat}-track`}>
        {items.map((a, i) => (
          <RailCard key={a.slug} a={a} i={i} big={i === 0} t={t} cat={cat} />
        ))}
        <Link to={`/category/${cat}`} className={`snap-start shrink-0 w-[60vw] sm:w-[260px] aspect-[3/4] border-[3px] ${t.rule} grid place-items-center group hover:bg-hot hover:text-ink transition-colors`} data-testid={`rail-${cat}-more`}>
          <span className="display text-4xl text-center px-4">More {catLabel(cat)} <ArrowUpRight className="inline h-8 w-8 group-hover:rotate-45 transition-transform" /></span>
        </Link>
      </div>
    </section>
  );
};
